/**
 * SOLID Review
 * S — Strong. One concern: the referral-code field — its value, the check on blur (one at a time), and the code the sign-up sends.
 * O — Good. A new validation rule is a change inside `blur`.
 * L — Strong. A view model (`IServiceViewModel`).
 * I — Strong. Two handlers and the view-model surface; the sign-up reads the snapshot's code.
 * D — Strong. One use case and the request contexts.
 */
import type { IServiceViewModel } from './IServiceViewModel';
import { ObservableState } from './ObservableState';
import type { IPortClientUseCases, TClientUseCaseInput } from '../../application/ports/ClientUseCases.contract';
import type { ClientRequests } from '../client/ClientRequests';

type ReferralStatus = 'idle' | 'valid' | 'invalid';

export interface IStateReferral {
  readonly referralCode: string;
  readonly referralStatus: ReferralStatus;
}

const EMPTY: IStateReferral = { referralCode: '', referralStatus: 'idle' };
const IDLE: Partial<IStateReferral> = { referralStatus: 'idle' };

/**
 * THE REFERRAL FIELD (frontend-framework-oo P2, 2026-10-29, §28.294) — `hooks/useReferralValidation.ts` (frontend strict-ports FE3:
 * one hook over the `checkReferral` use case) as a controller: the code upper-cased and cut to ten as typed; checked on blur, one check
 * at a time; a check that fails reads as "not checked" (idle). `validatedReferral` — what the sign-up sends — is the code only when
 * the server found it valid.
 */
export class ReferralValidation implements IServiceViewModel<IStateReferral> {
  private readonly state = new ObservableState<IStateReferral>(EMPTY);
  private validating = false;

  constructor(
    private readonly useCases: Pick<IPortClientUseCases, 'checkReferral'>,
    private readonly requests: ClientRequests,
  ) {}

  /** What the sign-up sends: the trimmed code if the server found it valid, otherwise nothing. */
  static validatedOf(state: IStateReferral): string | undefined {
    return state.referralStatus === 'valid' ? state.referralCode.trim() : undefined;
  }

  readonly change = (typed: string): void => {
    const referralCode = typed.toUpperCase().slice(0, 10);
    const changed: Partial<IStateReferral> = referralCode ? { referralCode } : { referralCode, referralStatus: 'idle' };
    this.state.update(changed);
  };

  readonly blur = async (): Promise<void> => {
    const code = this.state.get().referralCode.trim();
    if (!code) {
      this.state.update(IDLE);
      return;
    }
    /* c8 ignore next -- concurrent-call guard; not reproducible without a real async race in unit tests */
    if (this.validating) return;
    this.validating = true;
    try {
      const command: TClientUseCaseInput<'checkReferral'> = { code };
      const checked = await this.useCases.checkReferral(this.requests.context(), command);
      const status: Partial<IStateReferral> = { referralStatus: !checked.success ? 'idle' : checked.value.valid ? 'valid' : 'invalid' };
      this.state.update(status);
    } finally {
      this.validating = false;
    }
  };

  getSnapshot(): IStateReferral {
    return this.state.get();
  }

  subscribe(listener: () => void): () => void {
    return this.state.subscribe(listener);
  }

  attach(): void {}

  detach(): void {}
}
